import type { ComputedRef, Ref } from 'vue';
import { isArray } from 'lodash-es';
import {
  computed,

  ref,

} from 'vue';

import type { IFormPlainDataListboxPropsChangeState } from '@modules/forms/types';
import type {
  IReportChangeInnerTabPayload,
  IReportInnerTabResponse,
  IReportQueryOptions,
  IReportTabItem,
} from '@modules/reports/types';
import type { ISmartFilterColumn, ISmartFilterColumnData, ISmartFilterEmitsDataValues } from '@modules/smart-filters/types';
import { useI18n } from '@modules/i18n/composables';
import { STATS_COLUMNS } from '@modules/reports/components/VReports/constants';
import { useReportChangeInnerTabMutation } from '@modules/reports/mutations';
import { useReportInnerTabQuery } from '@modules/reports/queries';
import { isSmartFilterCheckboxColumnIdData, isSmartFilterCompareColumnIdData, isSmartFilterDateColumnIdData } from '@modules/smart-filters/utils';
import { watchImmediate } from '@vueuse/core';

import type { IReportTabContentEmits } from '../types';
import { isObjectEmpty } from '../utils';

export interface ReportTabContentComposable {
  selectedFilterColumnId: Ref<string | undefined>;
  innerTabData: Ref<IReportInnerTabResponse | undefined>;
  selectedStatsColumnData: ComputedRef<ISmartFilterColumnData | undefined>;
  statsColumns: ComputedRef<ISmartFilterColumn[]>;
  isInnerTabLoading: Ref<boolean>;
  innerTabFetching: Ref<boolean>;
  isChangeInnerTabLoading: Ref<boolean>;
  isStatsColumnChangeLoading: Ref<boolean>;
  isDialogOpened: Ref<boolean>;
  onSelectStatsColumn(columnId: string): void;
  onChangeStatsData(data: ISmartFilterEmitsDataValues): Promise<void>;
  onInnerTabDataChange(data: IFormPlainDataListboxPropsChangeState<string | number>): void;
  toggleDialog(value: boolean): void;
}

export default function useReportTabContent(
  activeTab: Ref<IReportTabItem>,
  activeInnerTab: Ref<IReportTabItem>,
  queryOptions: Ref<IReportQueryOptions>,
  isSegmentsType: Ref<boolean>,
  translationKey: Ref<symbol>,
  emit: IReportTabContentEmits,
): ReportTabContentComposable {
  const { getTranslation } = useI18n();

  const selectedFilterColumnId = ref<string>();
  const isDialogOpened = ref(false);
  const isStatsColumnChangeLoading = ref(false);

  const tabId = computed(() => activeTab.value.id);
  const innerTabId = computed(() => activeInnerTab.value.id);

  const {
    data: innerTabData,
    isLoading: isInnerTabLoading,
    isFetching: isInnerTabFetching,
    refetch,
  } = useReportInnerTabQuery(queryOptions, tabId, innerTabId);

  const { mutateAsync: changeInnerTabData, isLoading: isChangeInnerTabLoading } = useReportChangeInnerTabMutation();

  const innerTabFetching = computed(() => (activeInnerTab.value?.advancedFilter ? isInnerTabLoading.value : isInnerTabFetching.value));

  const selectedStatsColumnData = computed<ISmartFilterColumnData | undefined>(() => {
    if (!isArray(innerTabData.value) && selectedFilterColumnId.value) {
      return innerTabData.value?.[selectedFilterColumnId.value];
    }
    return undefined;
  });

  const statsColumns = computed<ISmartFilterColumn[]>(() => STATS_COLUMNS.map((column) => (
    {
      id: String(column.id),
      type: column.type,
      label: getTranslation(translationKey.value, column.shortcode).value,
      isEmpty: isStatsColumnEmpty(column),
    }
  )));

  function isStatsColumnEmpty(column: ISmartFilterColumn): boolean {
    if (isArray(innerTabData.value))
      return true;

    const data = innerTabData.value?.[column.id];

    if (isSmartFilterCheckboxColumnIdData(column, data)) {
      return !data?.some((item) => item.check);
    }

    if (isSmartFilterCompareColumnIdData(column, data) || isSmartFilterDateColumnIdData(column, data)) {
      return isObjectEmpty(data);
    }
    return true;
  }

  function onInnerTabDataChange({ value, search, check }: IFormPlainDataListboxPropsChangeState<string | number>): void {
    const payload: IReportChangeInnerTabPayload = {
      options: queryOptions.value,
      tabId: tabId.value,
      innerTabId: innerTabId.value,
      add: check,
    };

    if (isSegmentsType.value) {
      payload.segment_tab = { data: { value, search } };
    } else {
      payload.structure_tab = { data: { value, search } };
    }

    void changeInnerTabData(payload);
  }

  async function onChangeStatsData({
    columnName,
    data,
    needToAdd,
  }: ISmartFilterEmitsDataValues): Promise<void> {
    if (!activeInnerTab.value?.advancedFilter)
      return;

    isStatsColumnChangeLoading.value = true;

    const payload: IReportChangeInnerTabPayload = {
      options: queryOptions.value,
      add: needToAdd,
      tabId: tabId.value,
      innerTabId: columnName,
      segment_tab: { data },
    };

    await changeInnerTabData(payload);
    await refetch();

    isStatsColumnChangeLoading.value = false;
  }

  function onSelectStatsColumn(columnId: string): void {
    selectedFilterColumnId.value = columnId;
  }

  function toggleDialog(value: boolean): void {
    isDialogOpened.value = value;
  }

  watchImmediate(innerTabFetching, (newValue) => {
    emit('innerTabFetching', newValue);
  });

  return {
    selectedFilterColumnId,
    innerTabData,
    selectedStatsColumnData,
    statsColumns,
    isInnerTabLoading,
    innerTabFetching,
    isChangeInnerTabLoading,
    isStatsColumnChangeLoading,
    isDialogOpened,
    onSelectStatsColumn,
    onChangeStatsData,
    onInnerTabDataChange,
    toggleDialog,
  };
}
